type Callback = (...args: unknown[]) => void

interface Callbacks {
  [namespace: string]: {
    [event: string]: Callback[]
  }
}

export default class EventEmitter {
  private callbacks: Callbacks

  constructor() {
    this.callbacks = { base: {} }
  }

  on(names: string, callback: Callback): this | false {
    // Errors
    if (!names) {
      console.warn("wrong names")
      return false
    }

    if (typeof callback !== "function") {
      console.warn("wrong callback")
      return false
    }

    // Resolve names
    const resolvedNames = this.resolveNames(names)

    // Each name
    resolvedNames.forEach((name) => {
      const resolvedName = this.resolveName(name)

      // Create namespace if not exist
      if (!(this.callbacks[resolvedName.namespace] instanceof Object)) {
        this.callbacks[resolvedName.namespace] = {}
      }

      // Create callback array if not exist
      if (
        !(
          this.callbacks[resolvedName.namespace][resolvedName.value] instanceof
          Array
        )
      ) {
        this.callbacks[resolvedName.namespace][resolvedName.value] = []
      }

      // Add callback
      this.callbacks[resolvedName.namespace][resolvedName.value].push(callback)
    })

    return this
  }

  off(names: string): this | false {
    // Errors
    if (!names) {
      console.warn("wrong name")
      return false
    }

    // Resolve names
    const resolvedNames = this.resolveNames(names)

    // Each name
    resolvedNames.forEach((name) => {
      const resolvedName = this.resolveName(name)

      // Remove namespace
      if (resolvedName.namespace !== "base" && resolvedName.value === "") {
        delete this.callbacks[resolvedName.namespace]
      } else {
        // Default case for 'base' namespace
        if (resolvedName.namespace === "base") {
          for (const namespace in this.callbacks) {
            if (
              this.callbacks[namespace] instanceof Object &&
              Array.isArray(this.callbacks[namespace][resolvedName.value])
            ) {
              delete this.callbacks[namespace][resolvedName.value]

              // Remove namespace if empty
              if (Object.keys(this.callbacks[namespace]).length === 0) {
                delete this.callbacks[namespace]
              }
            }
          }
        } else if (
          this.callbacks[resolvedName.namespace] instanceof Object &&
          Array.isArray(
            this.callbacks[resolvedName.namespace][resolvedName.value],
          )
        ) {
          delete this.callbacks[resolvedName.namespace][resolvedName.value]

          // Remove namespace if empty
          if (
            Object.keys(this.callbacks[resolvedName.namespace]).length === 0
          ) {
            delete this.callbacks[resolvedName.namespace]
          }
        }
      }
    })

    return this
  }

  trigger(name: string, args?: unknown[]) {
    // Errors
    if (!name) {
      console.warn("wrong name")
      return null
    }

    let finalResult = null

    // Default args
    const eventArgs = Array.isArray(args) ? args : []

    // Resolve names (should only have one event)
    const resolvedNames = this.resolveNames(name)
    const resolvedName = this.resolveName(resolvedNames[0])

    // Default namespace handling
    if (resolvedName.namespace === "base") {
      for (const namespace in this.callbacks) {
        if (
          this.callbacks[namespace] instanceof Object &&
          Array.isArray(this.callbacks[namespace][resolvedName.value])
        ) {
          for (const callback of this.callbacks[namespace][
            resolvedName.value
          ]) {
            const result = callback(...eventArgs)
            finalResult ??= result // Assign finalResult only if it's still null or undefined
          }
        }
      }
    } else if (this.callbacks[resolvedName.namespace] instanceof Object) {
      if (resolvedName.value === "") {
        console.warn("wrong name")
        return null
      }

      for (const callback of this.callbacks[resolvedName.namespace][
        resolvedName.value
      ]) {
        const result = callback(...eventArgs)
        finalResult ??= result // Assign finalResult only if it's still null or undefined
      }
    }

    return finalResult
  }

  private resolveNames(names: string): string[] {
    names = names.replace(/[^a-zA-Z0-9 ,/.]/g, "")
    names = names.replace(/[,/]+/g, " ")
    return names.split(" ").filter(Boolean) // Filter to remove empty strings
  }

  private resolveName(name: string): {
    original: string
    value: string
    namespace: string
  } {
    const parts = name.split(".")

    return {
      original: name,
      value: parts[0],
      namespace: parts.length > 1 && parts[1] !== "" ? parts[1] : "base",
    }
  }
}
